import React, { useMemo, useState } from 'react';
import { columnLetter, formatRange, previousMonth } from './sncShared';

/**
 * The three questions the upload can ask (spec §6). Each answer is re-posted
 * with the same file; the server keeps no state between prompts.
 */

const ROLES = [
  { key: 'name', label: 'Dish name', required: true },
  { key: 'qty', label: 'Quantity sold', required: true },
  { key: 'amount', label: 'Amount (₹)', required: true },
  { key: 'date', label: 'Date', required: false },
];

function Modal({ titleId, title, children }) {
  return (
    <div className="snc-modal-overlay" role="presentation">
      <div className="snc-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h3 id={titleId} className="snc-modal__title">{title}</h3>
        {children}
      </div>
    </div>
  );
}

const toIndex = (value) => (value === '' || value === null || value === undefined ? null : Number(value));

/** 409 NEEDS_COLUMNS: first 10 rows plus a column picker per role. */
export function ColumnConfirmModal({ fileName, sample, guesses, headerRowIndex, busy, onConfirm, onCancel }) {
  const rows = useMemo(() => (Array.isArray(sample) ? sample.slice(0, 10) : []), [sample]);
  const columnCount = useMemo(() => rows.reduce((max, r) => Math.max(max, Array.isArray(r) ? r.length : 0), 0), [rows]);
  const headerRow = Number.isInteger(headerRowIndex) && rows[headerRowIndex] ? rows[headerRowIndex] : null;
  const columns = useMemo(() => Array.from({ length: columnCount }, (_, index) => {
    const head = headerRow && headerRow[index] != null ? String(headerRow[index]).trim() : '';
    return { index, label: head ? `${columnLetter(index)} · ${head}` : `Column ${columnLetter(index)}` };
  }), [columnCount, headerRow]);

  const g = guesses || {};
  const [map, setMap] = useState({
    name: g.name ?? '',
    qty: g.qty ?? '',
    amount: g.amount ?? '',
    date: g.date ?? '',
  });
  const [error, setError] = useState('');

  const confirm = () => {
    const picked = {
      name: toIndex(map.name),
      qty: toIndex(map.qty),
      amount: toIndex(map.amount),
      date: toIndex(map.date),
    };
    const missing = ROLES.filter((r) => r.required && picked[r.key] === null).map((r) => r.label);
    if (missing.length) { setError(`Pick a column for: ${missing.join(', ')}.`); return; }
    const used = Object.values(picked).filter((v) => v !== null);
    if (new Set(used).size !== used.length) { setError('Each column can be used for one thing only.'); return; }
    setError('');
    onConfirm({ ...picked, headerRowIndex: Number.isInteger(headerRowIndex) ? headerRowIndex : null });
  };

  return (
    <Modal titleId="snc-columns-title" title="Which columns hold the sales?">
      <p className="snc-modal__text">
        We could not recognise the layout of <strong>{fileName}</strong>. Pick the column for each item below.
        This choice is remembered for files from the same POS.
      </p>
      <div className="snc-sample">
        <table>
          <thead>
            <tr>
              <th aria-label="Row" />
              {columns.map((c) => <th key={c.index}>{columnLetter(c.index)}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri} className={ri === headerRowIndex ? 'snc-sample__header' : undefined}>
                <td className="snc-sample__rownum">{ri + 1}</td>
                {columns.map((c) => (
                  <td key={c.index}>{Array.isArray(r) && r[c.index] != null ? String(r[c.index]) : ''}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="snc-roles">
        {ROLES.map((role) => (
          <label key={role.key} className="snc-role">
            <span className="snc-role__label">{role.label}{role.required ? '' : ' (optional)'}</span>
            <select
              className="snc-select"
              value={map[role.key] === null ? '' : String(map[role.key])}
              disabled={busy}
              onChange={(e) => setMap((prev) => ({ ...prev, [role.key]: e.target.value }))}
            >
              <option value="">{role.required ? 'Choose a column…' : 'Not in this file'}</option>
              {columns.map((c) => <option key={c.index} value={String(c.index)}>{c.label}</option>)}
            </select>
          </label>
        ))}
      </div>
      {error && <p className="snc-error">{error}</p>}
      <div className="snc-modal__actions">
        <button type="button" className="snc-btn snc-btn--outline" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="snc-btn snc-btn--primary" onClick={confirm} disabled={busy}>
          {busy ? 'Reading…' : 'Use these columns'}
        </button>
      </div>
    </Modal>
  );
}

/** 409 NEEDS_MONTH: no date column, header or file-name date — the owner picks the month. */
export function MonthPickerModal({ fileName, busy, onConfirm, onCancel }) {
  const [month, setMonth] = useState(previousMonth());
  const valid = /^\d{4}-\d{2}$/.test(month);
  return (
    <Modal titleId="snc-month-title" title="Which month is this?">
      <p className="snc-modal__text">
        <strong>{fileName}</strong> does not say which dates it covers. Pick the month of these sales.
      </p>
      <label className="snc-role">
        <span className="snc-role__label">Month</span>
        <input
          type="month"
          className="snc-input"
          value={month}
          disabled={busy}
          onChange={(e) => setMonth(e.target.value)}
        />
      </label>
      <div className="snc-modal__actions">
        <button type="button" className="snc-btn snc-btn--outline" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="snc-btn snc-btn--primary" onClick={() => valid && onConfirm(month)} disabled={busy || !valid}>
          {busy ? 'Reading…' : 'Use this month'}
        </button>
      </div>
    </Modal>
  );
}

/** 409 DATE_OVERLAP: replace the earlier file, or keep both. */
export function OverlapModal({ fileName, existing, busy, onReplace, onAdd, onCancel }) {
  const prior = existing || {};
  return (
    <Modal titleId="snc-overlap-title" title="These dates are already uploaded">
      <p className="snc-modal__text">
        <strong>{fileName}</strong> overlaps <strong>{prior.fileName || 'an earlier file'}</strong>
        {prior.dateFrom || prior.dateTo ? ` (${formatRange(prior.dateFrom, prior.dateTo)})` : ''}.
        Replace that file with this one, or add both? Adding both counts the shared days twice.
      </p>
      <div className="snc-modal__actions">
        <button type="button" className="snc-btn snc-btn--outline" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="snc-btn snc-btn--outline" onClick={onAdd} disabled={busy}>Add both</button>
        <button type="button" className="snc-btn snc-btn--primary" onClick={onReplace} disabled={busy}>
          {busy ? 'Reading…' : 'Replace'}
        </button>
      </div>
    </Modal>
  );
}
